// Stable identifiers + current versions of every legal document Vertu
// presents to a user (tenant or guest). When a document is updated,
// bump its version here so the next consent capture records the new
// version against the user.
//
// Versions follow ISO date convention. The same date can repeat across
// edits in a single day if the substance hasn't changed; bump the date
// whenever the tick-box text or the document's substance changes.

export const LEGAL_DOCUMENT_VERSIONS = {
  privacy_policy: "2026-05-07",
  msa: "2026-05-07",
  dpa: "2026-05-07",
  sub_processors: "2026-05-07",
  // Bumped 2026-10-10: section 3 now covers offers and news by email, SMS
  // and WhatsApp on an opt-out basis for customers (POPIA s69(3)), the
  // opt-out routes (STOP / unsubscribe / unticked box) and WhatsApp delivery
  // through Meta.
  guest_terms: "2026-10-10",
  // The marketing-consent statement (its own document class, distinct from the
  // privacy policy or MSA). Bumped when the opt-in wording materially changes.
  // Since 2 Oct 2026 this is the TENANT sign-up's tick only (Vertu's own
  // marketing to the venue); its words have not changed since 2026-07-06.
  marketing: "2026-07-06",
  // The GUEST's marketing tick (offers and news from a venue), its own key so
  // its version moves without re-versioning the tenant tick. 2026-10-02: the
  // opt-out sentence names what exists (slice 0.2d) — the link in a
  // marketing email or SMS, or STOP on WhatsApp — instead of "the link in any
  // message", which a WhatsApp template send does not carry. Every widget
  // writer reads it through documentSlugForGuestConsent; no reader compares
  // the version (the SQL readers key on consent_kind = 'marketing' and treat
  // only 'whatsapp-bot-1' specially).
  guest_marketing: "2026-10-02",
} as const;

export type LegalDocumentSlug = keyof typeof LEGAL_DOCUMENT_VERSIONS;

// Public URLs. Until the marketing site is live these are placeholders;
// keep them stable so we don't break consent records when the public
// pages move.
export const LEGAL_DOCUMENT_URLS = {
  privacy_policy: "/legal/privacy",
  msa: "/legal/msa",
  dpa: "/legal/dpa",
  sub_processors: "/legal/subprocessors",
  guest_terms: "/legal/guest-terms",
  // Marketing rights are described within the privacy notice's direct-marketing
  // section until a standalone communications-preferences page exists.
  marketing: "/legal/privacy",
  guest_marketing: "/legal/privacy",
} as const;

// The exact text shown next to each tick-box. Change here, in one place,
// and both the UI and the consent log row reflect the same wording.
export const TENANT_SIGNUP_CONSENTS = {
  authorise_to_bind: {
    kind: "mandatory_terms",
    text:
      "I confirm that I am authorised to bind my organisation to these terms.",
    required: true,
  },
  msa_and_dpa: {
    kind: "mandatory_dpa",
    text:
      "I have read and agree to the Vertu Master Services Agreement and the Data Protection Schedule (POPIA / Operator Agreement).",
    required: true,
  },
  sub_processors_and_cross_border: {
    kind: "sub_processors",
    text:
      "I authorise Vertu to act as Operator in respect of my customers' Personal Information, and I authorise the engagement of the Sub-Operators listed at vertu.co.za/subprocessors, including the cross-border transfer of Personal Information to the European Union (Ireland) and Switzerland (Zurich) in accordance with section 72 of POPIA.",
    required: true,
  },
  guest_privacy_notice: {
    kind: "mandatory_privacy",
    text:
      "I confirm that I have, or will publish before going live, a privacy notice to my own customers that describes Vertu as my data Operator and the storage of their data in the EU and Switzerland.",
    required: true,
  },
  marketing_optin: {
    kind: "marketing",
    text:
      "I would like to receive product updates, hospitality-industry insights, and occasional marketing communications from Vertu. I can withdraw at any time from my account settings.",
    required: false,
  },
} as const;

export const GUEST_BOOKING_CONSENTS = {
  privacy_and_terms: {
    kind: "mandatory_terms",
    text:
      "I have read and agree to the venue's Privacy Notice and to the Vertu Guest Terms of Use. I understand my booking details are processed by the venue using the Vertu reservation platform and stored on secure servers in the European Union, with a read replica in Switzerland.",
    required: true,
  },
  // Marketing only. Transactional messages (booking confirmations +
  // reminders) are NOT bundled here — under POPIA s69 marketing consent must
  // be freely given and specific, so it cannot ride on a checkbox required to
  // book. Confirmations/reminders are sent as part of fulfilling the booking
  // and disclosed in the venue's Privacy Notice, not gated on this opt-in.
  //
  // The opt-out sentence names exactly what exists (slice 0.2d, 2 Oct 2026):
  // every marketing email carries an unsubscribe link (0.2a; newsletters the
  // same), every marketing SMS an opt-out link, and on WhatsApp the guest
  // replies STOP (a message sent through an approved WhatsApp template cannot
  // carry a link until Meta approves a new version, but STOP always works).
  // It used to say "the link in any message", which a WhatsApp template send
  // does not have. A material wording change, so the version is bumped (the
  // rule at the top of this file): this tick has its own document key,
  // guest_marketing (documentSlugForGuestConsent), so the tenant sign-up's
  // marketing tick keeps its key and version.
  marketing_optin: {
    kind: "marketing",
    text:
      "I'd like to receive occasional offers and news from this venue by email, SMS, or WhatsApp. I can opt out at any time using the link in any marketing email or SMS, or by replying STOP on WhatsApp.",
    required: false,
  },
} as const;

export type TenantConsentKey = keyof typeof TENANT_SIGNUP_CONSENTS;
export type GuestConsentKey = keyof typeof GUEST_BOOKING_CONSENTS;

/**
 * The legal document a tenant-signup consent checkbox refers to — used to stamp
 * document_slug/version on the consent_log row. Shared so the signup form and
 * the create-tenant consent replay agree on the mapping.
 */
export function documentSlugForConsent(
  key: TenantConsentKey,
): "msa" | "dpa" | "privacy_policy" | "sub_processors" | "marketing" {
  switch (key) {
    case "msa_and_dpa":
      return "dpa";
    case "sub_processors_and_cross_border":
      return "sub_processors";
    case "guest_privacy_notice":
      return "privacy_policy";
    case "marketing_optin":
      // Marketing consent is its own document class — not the MSA. Logging it
      // against a mandatory-terms slug muddies the consent-log audit.
      return "marketing";
    case "authorise_to_bind":
    default:
      return "msa";
  }
}

/**
 * The legal document a guest-booking consent checkbox refers to — used to stamp
 * document_slug/version on the consent_log row. Kept here (not inline in the
 * widget) so the mapping is a single source of truth, mirroring
 * documentSlugForConsent for tenant sign-up. Marketing opt-in maps to the
 * guest's own marketing statement, `guest_marketing` (since 2 Oct 2026; the
 * tenant tick keeps `marketing`) — NOT the privacy policy.
 */
export function documentSlugForGuestConsent(
  key: GuestConsentKey,
): "guest_terms" | "guest_marketing" {
  switch (key) {
    case "marketing_optin":
      return "guest_marketing";
    case "privacy_and_terms":
    default:
      return "guest_terms";
  }
}
